/**
 * Trunnion AI - Workflow Template API Endpoints
 */

import apiClient from './client';
import type {
  WorkflowTemplate,
  TemplateListResponse,
  TemplateInstantiateRequest,
  SaveAsTemplateRequest,
  TemplateFilters,
} from '@/types/template.types';
import type { WorkflowDefinition } from '@/types';

const BASE = '/workflows';

export const templatesApi = {
  /**
   * List workflow templates with optional filters and pagination.
   */
  list: async (filters?: TemplateFilters): Promise<TemplateListResponse> => {
    const params: Record<string, unknown> = {};
    if (filters?.category && filters.category !== 'all') {
      params.category = filters.category;
    }
    if (filters?.classification) {
      params.classification = filters.classification;
    }
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.page) {
      params.page = filters.page;
    }
    if (filters?.pageSize) {
      params.pageSize = filters.pageSize;
    }
    const { data } = await apiClient.get(`${BASE}/templates`, { params });
    return data;
  },

  /**
   * Get full details of a single template.
   */
  getById: async (id: string): Promise<WorkflowTemplate> => {
    const { data } = await apiClient.get(`${BASE}/templates/${id}`);
    return data;
  },

  /**
   * Create a new workflow from a template.
   */
  instantiate: async (
    templateId: string,
    request: TemplateInstantiateRequest,
  ): Promise<WorkflowDefinition> => {
    const { data } = await apiClient.post(
      `${BASE}/templates/${templateId}/instantiate`,
      request,
    );
    return data;
  },

  /**
   * Save an existing workflow as a reusable template.
   */
  saveAsTemplate: async (
    workflowId: string,
    request: SaveAsTemplateRequest,
  ): Promise<WorkflowTemplate> => {
    const { data } = await apiClient.post(
      `${BASE}/${workflowId}/save-as-template`,
      request,
    );
    return data;
  },
};
